/**
 * CL-ACCESS-FINANCIALS-001B — Staff financial authorization (fail-closed) and UI canonicalization.
 */

import { normalizeCollaboratorPermissions, isFullProjectAccessScope } from "./projectCollaboratorPermissions";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import { CLIENT_PORTAL_MODULE_PERMISSION_KEYS } from "./clientPortalModuleApplicabilityModel";
import { resolveSectionAccessMap } from "./projectCollaboratorAccessResolutionModel";
import {
  CLIENT_FINANCIAL_MODULE_PERMISSION_KEY,
  LEGACY_INVOICE_PERMISSION_KEY,
  RESERVED_STAFF_FINANCIAL_PERMISSION_KEYS,
  STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS,
  STAFF_FINANCIAL_VIEW_PERMISSION_KEYS,
  STAFF_INVOICE_WORKFLOW_PERMISSION_KEYS,
  isCustomAccessScope,
  readExplicitBoolean,
  readExplicitFalse,
} from "./projectStaffFinancialPermissionsModel";

const INACTIVE_STATUSES = new Set(["inactive", "removed", "paused"]);

function safeStr(value) {
  return String(value ?? "").trim().toLowerCase();
}

function emptyCapabilities() {
  return {
    canAccessFinancials: false,
    canViewInvoices: false,
    canViewBudget: false,
    canManageBudget: false,
    canAccessTimeLog: false,
    canViewTimeLog: false,
    canManageTimeEntries: false,
    canPrepareInvoices: false,
    canReviewInvoices: false,
    canApproveInvoices: false,
    canShareClientTest: false,
    canIssueInvoices: false,
    canManageInvoiceSettings: false,
    canViewReceipts: false,
    canManageReceipts: false,
    canViewPayments: false,
    canRecordPayments: false,
    legacyCanManageInvoices: false,
  };
}

function chainOk(flags = {}) {
  return Object.values(flags).every(Boolean);
}

function resolvedStaffFlag(rawPermissions, key, sections, accountingPermissionsUiEnabled) {
  if (readExplicitFalse(rawPermissions, key)) return false;
  if (!readExplicitBoolean(rawPermissions, key)) return false;
  if (key === "canViewInvoices" || key === "canViewBudget") {
    return sections.canAccessFinancials === true;
  }
  if (key === "canViewTimeLog") {
    return sections.canAccessTimeLog === true;
  }
  if (key === "canViewReceipts" || key === "canViewPayments") {
    return sections.canAccessFinancials === true;
  }
  return true;
}

function evaluateGateOnCapabilities({
  rawPermissions = {},
  accessScope = "full_project",
  role = "collaborator",
  accountingPermissionsUiEnabled = null,
} = {}) {
  const sections = resolveSectionAccessMap({
    rawPermissions,
    accessScope,
    role,
    accountingPermissionsUiEnabled,
  });
  const custom = isCustomAccessScope(accessScope);
  const legacyNormalized = normalizeCollaboratorPermissions(rawPermissions, { role, accessScope });

  const caps = emptyCapabilities();
  caps.canAccessFinancials = sections.canAccessFinancials === true;
  caps.canAccessTimeLog = sections.canAccessTimeLog === true;

  caps.canViewInvoices = resolvedStaffFlag(rawPermissions, "canViewInvoices", sections, accountingPermissionsUiEnabled);
  caps.canViewBudget = resolvedStaffFlag(rawPermissions, "canViewBudget", sections, accountingPermissionsUiEnabled);
  caps.canViewTimeLog = resolvedStaffFlag(rawPermissions, "canViewTimeLog", sections, accountingPermissionsUiEnabled);
  caps.canViewReceipts = resolvedStaffFlag(rawPermissions, "canViewReceipts", sections, accountingPermissionsUiEnabled);
  caps.canViewPayments = resolvedStaffFlag(rawPermissions, "canViewPayments", sections, accountingPermissionsUiEnabled);

  caps.canManageBudget = chainOk({
    access: caps.canAccessFinancials,
    view: caps.canViewBudget,
    manage: readExplicitBoolean(rawPermissions, "canManageBudget"),
  });
  caps.canManageTimeEntries = chainOk({
    access: sections.canAccessTimeLog === true,
    view: caps.canViewTimeLog,
    manage: readExplicitBoolean(rawPermissions, "canManageTimeEntries"),
  });
  caps.canManageReceipts = chainOk({
    access: caps.canAccessFinancials,
    view: caps.canViewReceipts,
    manage: readExplicitBoolean(rawPermissions, "canManageReceipts"),
  });
  caps.canRecordPayments = chainOk({
    access: caps.canAccessFinancials,
    view: caps.canViewPayments,
    manage: readExplicitBoolean(rawPermissions, "canRecordPayments"),
  });

  const explicitPrepare = readExplicitBoolean(rawPermissions, "canPrepareInvoices");
  const legacyPrepare =
    !custom &&
    legacyNormalized.canManageInvoices === true &&
    isFullProjectAccessScope(accessScope);

  caps.legacyCanManageInvoices = legacyNormalized.canManageInvoices === true;

  if (legacyPrepare) {
    caps.canAccessFinancials = true;
    caps.canViewInvoices = true;
  }

  caps.canPrepareInvoices = chainOk({
    access: caps.canAccessFinancials,
    view: caps.canViewInvoices,
    prepare: explicitPrepare || legacyPrepare,
  });

  caps.canReviewInvoices = chainOk({
    access: caps.canAccessFinancials,
    view: caps.canViewInvoices,
    review: readExplicitBoolean(rawPermissions, "canReviewInvoices"),
  });
  caps.canApproveInvoices = chainOk({
    review: caps.canReviewInvoices,
    approve: readExplicitBoolean(rawPermissions, "canApproveInvoices"),
  });
  caps.canShareClientTest = chainOk({
    review: caps.canReviewInvoices,
    share: readExplicitBoolean(rawPermissions, "canShareClientTest"),
  });
  caps.canIssueInvoices = chainOk({
    approve: caps.canApproveInvoices,
    issue: readExplicitBoolean(rawPermissions, "canIssueInvoices"),
  });
  caps.canManageInvoiceSettings = chainOk({
    prepare: caps.canPrepareInvoices,
    settings: readExplicitBoolean(rawPermissions, "canManageInvoiceSettings"),
  });

  if (custom && readExplicitBoolean(rawPermissions, LEGACY_INVOICE_PERMISSION_KEY) && !explicitPrepare) {
    caps.canPrepareInvoices = false;
    caps.canManageInvoiceSettings = false;
    if (!readExplicitBoolean(rawPermissions, "canViewInvoices")) {
      caps.canViewInvoices = false;
    }
    if (!readExplicitBoolean(rawPermissions, "canAccessFinancials")) {
      caps.canAccessFinancials = false;
    }
  }

  return caps;
}

function evaluateGateOffCapabilities({ rawPermissions = {}, accessScope = "full_project", role = "collaborator" } = {}) {
  const legacyNormalized = normalizeCollaboratorPermissions(rawPermissions, { role, accessScope });
  const caps = emptyCapabilities();
  caps.legacyCanManageInvoices = legacyNormalized.canManageInvoices === true;
  caps.canPrepareInvoices = legacyNormalized.canManageInvoices === true;
  caps.canViewTimeLog = legacyNormalized.canViewTimeLog === true;
  caps.canManageTimeEntries = legacyNormalized.canLogTime === true;
  caps.canAccessTimeLog = legacyNormalized.canLogTime === true || legacyNormalized.canViewTimeLog === true;
  caps.canAccessFinancials = legacyNormalized.canManageInvoices === true;
  caps.canViewInvoices = legacyNormalized.canManageInvoices === true;
  return caps;
}

function isUsableAssignmentRecord(assignment) {
  return assignment != null && typeof assignment === "object";
}

export function evaluateStaffFinancialCapabilities({
  assignment = {},
  accountingPermissionsUiEnabled = null,
} = {}) {
  if (!isUsableAssignmentRecord(assignment)) {
    return emptyCapabilities();
  }
  if (Object.keys(assignment).length === 0) {
    return emptyCapabilities();
  }

  const status = safeStr(assignment.status || "active");
  if (INACTIVE_STATUSES.has(status)) {
    return emptyCapabilities();
  }

  const rawPermissions = assignment.permissions || {};
  const accessScope = assignment.accessScope || "full_project";
  const role = assignment.role || "collaborator";

  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return evaluateGateOffCapabilities({ rawPermissions, accessScope, role });
  }

  return evaluateGateOnCapabilities({
    rawPermissions,
    accessScope,
    role,
    accountingPermissionsUiEnabled,
  });
}

export function clientFinancialModuleGrantsStaffAccess(rawPermissions = {}) {
  return rawPermissions?.[CLIENT_FINANCIAL_MODULE_PERMISSION_KEY] === true;
}

export function clientViewFinancialsDoesNotGrantStaffInvoiceAccess({
  assignment = {},
  accountingPermissionsUiEnabled = null,
} = {}) {
  const raw = assignment.permissions || {};
  if (raw[CLIENT_FINANCIAL_MODULE_PERMISSION_KEY] !== true) {
    return true;
  }
  const caps = evaluateStaffFinancialCapabilities({ assignment, accountingPermissionsUiEnabled });
  return caps.canViewInvoices !== true && caps.canPrepareInvoices !== true;
}

const PARENT_IMPLIES = Object.freeze([
  { child: "canManageBudget", parents: ["canAccessFinancials", "canViewBudget"] },
  { child: "canManageTimeEntries", parents: ["canAccessTimeLog", "canViewTimeLog"] },
  { child: "canPrepareInvoices", parents: ["canAccessFinancials", "canViewInvoices", "canAccessTimeLog", "canViewTimeLog"] },
  { child: "canReviewInvoices", parents: ["canAccessFinancials", "canViewInvoices"] },
  { child: "canApproveInvoices", parents: ["canReviewInvoices"] },
  { child: "canShareClientTest", parents: ["canReviewInvoices"] },
  { child: "canIssueInvoices", parents: ["canApproveInvoices"] },
  { child: "canManageInvoiceSettings", parents: ["canPrepareInvoices"] },
  { child: "canManageReceipts", parents: ["canAccessFinancials", "canViewReceipts"] },
  { child: "canRecordPayments", parents: ["canAccessFinancials", "canViewPayments"] },
]);

/**
 * UI/preset canonicalization — may add required parents when gate is enabled.
 * Not used for authorization evaluation.
 */
export function canonicalizeStaffFinancialPermissionsForUi(
  rawPermissions = {},
  { accountingPermissionsUiEnabled = null } = {}
) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return { ...rawPermissions };
  }

  const next = { ...rawPermissions };
  PARENT_IMPLIES.forEach(({ child, parents }) => {
    if (next[child] !== true) return;
    parents.forEach((parent) => {
      if (next[parent] === false) return;
      next[parent] = true;
    });
  });
  return next;
}

export function assignmentHasStaffFinancialIntent(rawPermissions = {}) {
  return [
    ...STAFF_FINANCIAL_VIEW_PERMISSION_KEYS,
    ...STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS,
    ...STAFF_INVOICE_WORKFLOW_PERMISSION_KEYS,
    ...RESERVED_STAFF_FINANCIAL_PERMISSION_KEYS,
    LEGACY_INVOICE_PERMISSION_KEY,
  ].some((key) => rawPermissions?.[key] === true);
}

export function listStaffFinancialUiPermissionKeys({ accountingPermissionsUiEnabled = null } = {}) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return [];
  }
  return [
    ...STAFF_FINANCIAL_VIEW_PERMISSION_KEYS,
    ...STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS,
    ...STAFF_INVOICE_WORKFLOW_PERMISSION_KEYS,
  ];
}

export function listReservedStaffFinancialPermissionKeys() {
  return [...RESERVED_STAFF_FINANCIAL_PERMISSION_KEYS];
}
